import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaUndo, FaPlus, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { LuCircleHelp } from 'react-icons/lu';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';
import PortalIconPicker from '../../../components/PortalCMS/PortalIconPicker';

const STEP_COLOR_OPTIONS = [
  { value: 'dark', label: 'Dark' },
  { value: 'primary', label: 'Primary' },
  { value: 'emerald', label: 'Emerald' },
  { value: 'blue', label: 'Blue' },
];

const CONTACT_COLOR_OPTIONS = [
  { value: 'primary', label: 'Primary' },
  { value: 'green', label: 'Green' },
  { value: 'blue', label: 'Blue' },
];

const emptyService = (order) => ({ name: '', status: '', icon: 'FaLock', active: false, order });
const emptyStep = (order) => ({ step: order + 1, title: '', status: 'pending', color: 'primary', order });
const emptyContact = (order) => ({ label: '', value: '', url: '', icon: 'FaEnvelope', color: 'primary', isActive: true, order });

const defaultForm = () => ({
  isPublished: true,
  welcomeTitlePrefix: 'Welcome to the ',
  brandHighlight: 'BooksComply',
  welcomeTitleSuffix: ' Help Portal!',
  welcomeIcon: 'LuCircleHelp',
  welcomeSubtitle: '',
  helpGuideSection: { title: 'Help Guide', icon: 'FaQuestionCircle', description: '' },
  pricingSection: { title: 'Pricing Plan: Services You Can Use', services: [] },
  taxFilingSection: { title: '', subtitle: '', steps: [] },
  supportSection: {
    title: '',
    intro: '',
    chatHighlight: 'Chat with us!',
    helpTitle: 'Need Help?',
    contacts: [],
    notificationTip: '',
    notificationIcon: 'LuBell',
  },
});

const HelpPortalCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('header');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState(defaultForm());

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/help`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const c = res.data.data.content || {};
        const base = defaultForm();
        setForm({
          isPublished: res.data.data.isPublished !== false,
          welcomeTitlePrefix: c.welcomeTitlePrefix ?? base.welcomeTitlePrefix,
          brandHighlight: c.brandHighlight ?? base.brandHighlight,
          welcomeTitleSuffix: c.welcomeTitleSuffix ?? base.welcomeTitleSuffix,
          welcomeIcon: c.welcomeIcon || base.welcomeIcon,
          welcomeSubtitle: c.welcomeSubtitle || base.welcomeSubtitle,
          helpGuideSection: { ...base.helpGuideSection, ...(c.helpGuideSection || {}) },
          pricingSection: {
            ...base.pricingSection,
            ...(c.pricingSection || {}),
            services: (c.pricingSection?.services || []).map((item, i) => ({ ...item, order: item.order ?? i })),
          },
          taxFilingSection: {
            ...base.taxFilingSection,
            ...(c.taxFilingSection || {}),
            steps: (c.taxFilingSection?.steps || []).map((item, i) => ({ ...item, order: item.order ?? i })),
          },
          supportSection: {
            ...base.supportSection,
            ...(c.supportSection || {}),
            contacts: (c.supportSection?.contacts || []).map((item, i) => ({ ...item, order: item.order ?? i })),
          },
        });
      }
    } catch (err) {
      toastError('Failed to load help portal content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateSection = (section, field, value) =>
    setForm((prev) => ({ ...prev, [section]: { ...prev[section], [field]: value } }));

  const updateList = (section, listKey, index, field, value) => {
    setForm((prev) => {
      const list = [...prev[section][listKey]];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, [section]: { ...prev[section], [listKey]: list } };
    });
  };

  const addListItem = (section, listKey, factory) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [listKey]: [...prev[section][listKey], factory(prev[section][listKey].length)],
      },
    }));
  };

  const removeListItem = (section, listKey, index) => {
    if (!window.confirm('Remove this item?')) return;
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [listKey]: prev[section][listKey]
          .filter((_, i) => i !== index)
          .map((item, i) => ({ ...item, order: i })),
      },
    }));
  };

  const moveListItem = (section, listKey, index, direction) => {
    const target = index + direction;
    const list = form[section][listKey];
    if (target < 0 || target >= list.length) return;
    setForm((prev) => {
      const items = [...prev[section][listKey]];
      [items[index], items[target]] = [items[target], items[index]];
      return {
        ...prev,
        [section]: { ...prev[section], [listKey]: items.map((item, i) => ({ ...item, order: i })) },
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const payload = {
        isPublished: form.isPublished,
        content: {
          welcomeTitlePrefix: form.welcomeTitlePrefix,
          brandHighlight: form.brandHighlight,
          welcomeTitleSuffix: form.welcomeTitleSuffix,
          welcomeIcon: form.welcomeIcon,
          welcomeSubtitle: form.welcomeSubtitle,
          helpGuideSection: form.helpGuideSection,
          pricingSection: {
            ...form.pricingSection,
            services: form.pricingSection.services.map((item, i) => ({ ...item, order: i })),
          },
          taxFilingSection: {
            ...form.taxFilingSection,
            steps: form.taxFilingSection.steps.map((item, i) => ({ ...item, step: i + 1, order: i })),
          },
          supportSection: {
            ...form.supportSection,
            contacts: form.supportSection.contacts.map((item, i) => ({ ...item, order: i })),
          },
          isPublished: form.isPublished,
        },
      };
      const res = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/help`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        toastSuccess('Help Guide content saved');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore preloaded Help Guide content? This overwrites your edits.')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/help/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        toastSuccess('Preloaded defaults restored');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  const tabs = [
    { id: 'header', label: 'Welcome' },
    { id: 'guide', label: 'Help Guide' },
    { id: 'pricing', label: 'Pricing & Tax' },
    { id: 'support', label: 'Support' },
  ];

  const ListControls = ({ onUp, onDown, onRemove, disableUp, disableDown }) => (
    <div className="flex items-center gap-1">
      <button type="button" onClick={onUp} disabled={disableUp} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowUp className="w-3 h-3" /></button>
      <button type="button" onClick={onDown} disabled={disableDown} className="p-2 rounded-lg border disabled:opacity-40"><FaArrowDown className="w-3 h-3" /></button>
      <button type="button" onClick={onRemove} className="p-2 rounded-lg text-red-500 hover:bg-red-50"><FaTrash className="w-3.5 h-3.5" /></button>
    </div>
  );

  return (
    <PageLayout>
      <PageHeader
        title="Help Guide — Portal CMS"
        description="Edit User Portal Help page sections, icons, cards, tax steps, and support contacts."
        icon={LuCircleHelp}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={() => navigate('/portal-cms/help')} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 text-sm">Back</button>
            <button type="button" onClick={handleReset} disabled={resetting || saving} className="px-4 py-2 rounded-xl border text-sm flex items-center gap-2">
              <FaUndo className="w-3.5 h-3.5" /> Reload Defaults
            </button>
            <button type="button" onClick={handleSave} disabled={saving} className="btn-premium flex items-center gap-2">
              <FaSave className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        }
      />

      {loading ? (
        <SkeletonLoader variant="page" />
      ) : (
        <div className="space-y-6">
          <div className="p-4 rounded-xl bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-800/40 text-sm text-sky-800 dark:text-sky-200">
            Account Settings content (sidebar + My Profile, Invoice, Payment History) is edited under Portal CMS → Account Settings section.
          </div>
          <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/40 text-sm text-amber-800 dark:text-amber-200">
            Documents section on Help Guide is edited from Portal CMS → Help Guide → Documents Section.
          </div>
          <div className="flex items-center justify-between bg-white dark:bg-dark-800 rounded-2xl border p-4">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input type="checkbox" checked={form.isPublished} onChange={(e) => updateField('isPublished', e.target.checked)} className="rounded" />
              Published on User Portal
            </label>
            <div className="flex gap-2 flex-wrap">
              {tabs.map((tab) => (
                <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`px-3 py-2 rounded-xl text-sm font-medium ${activeTab === tab.id ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-dark-700'}`}>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'header' && (
            <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-500">Title Before Brand</label>
                  <input className="input-modern mt-1" value={form.welcomeTitlePrefix} onChange={(e) => updateField('welcomeTitlePrefix', e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-500">Brand Highlight</label>
                  <input className="input-modern mt-1" value={form.brandHighlight} onChange={(e) => updateField('brandHighlight', e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-gray-500">Title After Brand</label>
                  <input className="input-modern mt-1" value={form.welcomeTitleSuffix} onChange={(e) => updateField('welcomeTitleSuffix', e.target.value)} />
                </div>
              </div>
              <PortalIconPicker value={form.welcomeIcon} onChange={(icon) => updateField('welcomeIcon', icon)} />
              <div>
                <label className="text-xs font-semibold uppercase text-gray-500">Subtitle</label>
                <textarea className="input-modern mt-1 min-h-[80px]" value={form.welcomeSubtitle} onChange={(e) => updateField('welcomeSubtitle', e.target.value)} />
              </div>
            </div>
          )}

          {activeTab === 'guide' && (
            <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-4">
              <input className="input-modern" value={form.helpGuideSection.title} onChange={(e) => updateSection('helpGuideSection', 'title', e.target.value)} placeholder="Section title" />
              <PortalIconPicker value={form.helpGuideSection.icon} onChange={(icon) => updateSection('helpGuideSection', 'icon', icon)} />
              <textarea className="input-modern min-h-[100px]" value={form.helpGuideSection.description} onChange={(e) => updateSection('helpGuideSection', 'description', e.target.value)} placeholder="Description" />
            </div>
          )}

          {activeTab === 'pricing' && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6">
                <label className="text-xs font-semibold uppercase text-gray-500">Pricing Section Title</label>
                <input className="input-modern mt-1" value={form.pricingSection.title} onChange={(e) => updateSection('pricingSection', 'title', e.target.value)} />
              </div>
              <div className="flex justify-between items-center">
                <h3 className="font-bold">Services</h3>
                <button type="button" onClick={() => addListItem('pricingSection', 'services', emptyService)} className="text-sm text-primary-600 flex items-center gap-1"><FaPlus /> Add Service</button>
              </div>
              {form.pricingSection.services.map((service, index) => (
                <div key={`service-${index}`} className="rounded-2xl border p-5 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm font-bold">Service {index + 1}</span>
                    <ListControls onUp={() => moveListItem('pricingSection', 'services', index, -1)} onDown={() => moveListItem('pricingSection', 'services', index, 1)} onRemove={() => removeListItem('pricingSection', 'services', index)} disableUp={index === 0} disableDown={index === form.pricingSection.services.length - 1} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input className="input-modern" value={service.name} onChange={(e) => updateList('pricingSection', 'services', index, 'name', e.target.value)} placeholder="Service name" />
                    <input className="input-modern" value={service.status} onChange={(e) => updateList('pricingSection', 'services', index, 'status', e.target.value)} placeholder="Status label" />
                  </div>
                  <PortalIconPicker value={service.icon} onChange={(icon) => updateList('pricingSection', 'services', index, 'icon', icon)} />
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={service.active} onChange={(e) => updateList('pricingSection', 'services', index, 'active', e.target.checked)} /> Active service (green highlight)</label>
                </div>
              ))}

              <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-4">
                <h3 className="font-bold">Tax Filing Steps</h3>
                <input className="input-modern" value={form.taxFilingSection.title} onChange={(e) => updateSection('taxFilingSection', 'title', e.target.value)} placeholder="Section title" />
                <textarea className="input-modern min-h-[60px]" value={form.taxFilingSection.subtitle} onChange={(e) => updateSection('taxFilingSection', 'subtitle', e.target.value)} placeholder="Subtitle" />
                <div className="flex justify-end">
                  <button type="button" onClick={() => addListItem('taxFilingSection', 'steps', emptyStep)} className="text-sm text-primary-600 flex items-center gap-1"><FaPlus /> Add Step</button>
                </div>
                {form.taxFilingSection.steps.map((step, index) => (
                  <div key={`step-${index}`} className="rounded-xl border p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm font-bold">Step {index + 1}</span>
                      <ListControls onUp={() => moveListItem('taxFilingSection', 'steps', index, -1)} onDown={() => moveListItem('taxFilingSection', 'steps', index, 1)} onRemove={() => removeListItem('taxFilingSection', 'steps', index)} disableUp={index === 0} disableDown={index === form.taxFilingSection.steps.length - 1} />
                    </div>
                    <input className="input-modern" value={step.title} onChange={(e) => updateList('taxFilingSection', 'steps', index, 'title', e.target.value)} placeholder="Step title" />
                    <div className="grid grid-cols-2 gap-3">
                      <select className="input-modern" value={step.status} onChange={(e) => updateList('taxFilingSection', 'steps', index, 'status', e.target.value)}>
                        <option value="pending">Pending</option>
                        <option value="in-progress">In Progress</option>
                      </select>
                      <select className="input-modern" value={step.color} onChange={(e) => updateList('taxFilingSection', 'steps', index, 'color', e.target.value)}>
                        {STEP_COLOR_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'support' && (
            <div className="space-y-4">
              <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-4">
                <input className="input-modern" value={form.supportSection.title} onChange={(e) => updateSection('supportSection', 'title', e.target.value)} placeholder="Section title" />
                <textarea className="input-modern min-h-[70px]" value={form.supportSection.intro} onChange={(e) => updateSection('supportSection', 'intro', e.target.value)} placeholder="Intro (mention chat button)" />
                <input className="input-modern" value={form.supportSection.chatHighlight} onChange={(e) => updateSection('supportSection', 'chatHighlight', e.target.value)} placeholder="Highlighted chat text e.g. Chat with us!" />
                <input className="input-modern" value={form.supportSection.helpTitle} onChange={(e) => updateSection('supportSection', 'helpTitle', e.target.value)} placeholder="Need Help? title" />
                <textarea className="input-modern min-h-[60px]" value={form.supportSection.notificationTip} onChange={(e) => updateSection('supportSection', 'notificationTip', e.target.value)} placeholder="Notification tip" />
                <PortalIconPicker value={form.supportSection.notificationIcon} onChange={(icon) => updateSection('supportSection', 'notificationIcon', icon)} />
              </div>
              <div className="flex justify-between items-center">
                <h3 className="font-bold">Contact Cards</h3>
                <button type="button" onClick={() => addListItem('supportSection', 'contacts', emptyContact)} className="text-sm text-primary-600 flex items-center gap-1"><FaPlus /> Add Contact</button>
              </div>
              {form.supportSection.contacts.map((contact, index) => (
                <div key={`contact-${index}`} className="rounded-2xl border p-5 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm font-bold">{contact.label || `Contact ${index + 1}`}</span>
                    <ListControls onUp={() => moveListItem('supportSection', 'contacts', index, -1)} onDown={() => moveListItem('supportSection', 'contacts', index, 1)} onRemove={() => removeListItem('supportSection', 'contacts', index)} disableUp={index === 0} disableDown={index === form.supportSection.contacts.length - 1} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input className="input-modern" value={contact.label} onChange={(e) => updateList('supportSection', 'contacts', index, 'label', e.target.value)} placeholder="Label e.g. Email" />
                    <input className="input-modern" value={contact.value} onChange={(e) => updateList('supportSection', 'contacts', index, 'value', e.target.value)} placeholder="Display value" />
                    <input className="input-modern md:col-span-2" value={contact.url} onChange={(e) => updateList('supportSection', 'contacts', index, 'url', e.target.value)} placeholder="Link URL" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <PortalIconPicker value={contact.icon} onChange={(icon) => updateList('supportSection', 'contacts', index, 'icon', icon)} />
                    <select className="input-modern" value={contact.color} onChange={(e) => updateList('supportSection', 'contacts', index, 'color', e.target.value)}>
                      {CONTACT_COLOR_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={contact.isActive !== false} onChange={(e) => updateList('supportSection', 'contacts', index, 'isActive', e.target.checked)} /> Active</label>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end">
            <button type="button" onClick={handleSave} disabled={saving} className="btn-premium flex items-center gap-2">
              <FaSave className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default HelpPortalCMS;
